import React from 'react'
import {useIntl} from 'react-intl'

import {NoStyleLink} from '@vio/atlas/src/components/designSystem/helpers/NoStyleLink'
import {Text} from '@vio/atlas/src/components/designSystem/Text'

import {TRUSTPILOT_LABELS, TRUSTPILOT_REVIEWS_URL} from './constants'
import {
  BadgeWrapper,
  BannerWrapper,
  CardWrapper,
  HeroWrapper,
  InlineLink,
  InlineWrapper,
  PanelWrapper,
  Separator,
  StyledText,
  SummaryWrapper
} from './styles'
import {TrustpilotSvg} from './TrustpilotSvg'
import {TrustpilotWidgetLoading} from './TrustpilotWidgetLoading'
import {getRateURL, getTrustScoreLabel} from './utils'

export type TrustPilotWidgetVariant =
  | 'summary'
  | 'banner'
  | 'hero'
  | 'inline'
  | 'panel'
  | 'card'
  | 'badge'
export type TrustPilotWidgetTheme = 'light' | 'dark'

interface Props {
  trustScore: number | undefined
  stars: number | undefined
  numberOfReviews: number | undefined
  variant?: TrustPilotWidgetVariant
  theme?: TrustPilotWidgetTheme
  isLoading?: boolean
  onClick?: () => void
}
export const TrustpilotWidget = ({
  variant = 'summary',
  theme = 'light',
  trustScore,
  numberOfReviews,
  stars,
  isLoading,
  onClick
}: Props) => {
  const {formatMessage} = useIntl()

  if (isLoading) return <TrustpilotWidgetLoading variant={variant} />

  if (!trustScore || !stars || !numberOfReviews) return null

  const scoreAlt = formatMessage(TRUSTPILOT_LABELS.score, {score: trustScore})

  switch (variant) {
    case 'banner':
      return (
        <BannerWrapper className={`--theme-${theme}`}>
          <Text variant="titleS">
            {formatMessage(getTrustScoreLabel(trustScore))}
          </Text>
          <img src={getRateURL(stars)} alt={scoreAlt} className="--stars" />
          <Text variant="labelS">
            {formatMessage(TRUSTPILOT_LABELS.score, {
              score: trustScore
            })}
          </Text>
          <Separator>·</Separator>

          <StyledText variant="labelS">
            {formatMessage(TRUSTPILOT_LABELS.reviews, {
              totalReviews: numberOfReviews,
              icon: <TrustpilotSvg />
            })}
          </StyledText>
        </BannerWrapper>
      )
    case 'inline':
      return (
        <InlineWrapper className={`--theme-${theme}`}>
          <img src={getRateURL(stars)} alt={scoreAlt} className="--stars" />
          <Text variant="labelS">
            {formatMessage(getTrustScoreLabel(trustScore))}
          </Text>
          <Separator>·</Separator>
          <Text variant="labelS">{trustScore}/5</Text>
          <Separator>·</Separator>
          <InlineLink
            onClick={() => onClick?.()}
            href={TRUSTPILOT_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Text variant="labelS">
              {formatMessage(TRUSTPILOT_LABELS.reviewsCount, {
                reviews: numberOfReviews
              })}
            </Text>
          </InlineLink>
        </InlineWrapper>
      )

    case 'panel':
      return (
        <PanelWrapper className={`--theme-${theme}`}>
          <StyledText variant="labelS">
            {formatMessage(TRUSTPILOT_LABELS.reviews, {
              totalReviews: numberOfReviews,
              icon: <TrustpilotSvg />
            })}
          </StyledText>
          <div className="--row">
            <Text variant="titleS">
              {formatMessage(getTrustScoreLabel(trustScore))}
            </Text>
            <img src={getRateURL(stars)} alt={scoreAlt} className="--stars" />
            <Text variant="labelS">
              {formatMessage(TRUSTPILOT_LABELS.score, {
                score: trustScore
              })}
            </Text>
          </div>
        </PanelWrapper>
      )
    case 'card':
      return (
        <CardWrapper
          className={`--theme-${theme}`}
          onClick={() => onClick?.()}
          href={TRUSTPILOT_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <TrustpilotSvg />
          <img src={getRateURL(stars)} alt={scoreAlt} className="--stars" />
          <div className="--score">
            <Text variant="titleXS">
              {formatMessage(TRUSTPILOT_LABELS.trustScore)}
            </Text>
            <Text variant="titleXS">{trustScore}</Text>
          </div>
          <Text variant="labelXS" className="--reviews-link">
            {formatMessage(TRUSTPILOT_LABELS.reviewsCount, {
              reviews: numberOfReviews
            })}
          </Text>
        </CardWrapper>
      )
    case 'badge':
      return (
        <BadgeWrapper
          className={`--theme-${theme}`}
          onClick={() => onClick?.()}
          href={TRUSTPILOT_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={getRateURL(stars)} alt={scoreAlt} className="--stars" />
          <TrustpilotSvg />
        </BadgeWrapper>
      )

    case 'hero':
      return (
        <NoStyleLink
          href={TRUSTPILOT_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <HeroWrapper className={`--theme-${theme}`}>
            <div className="--score-block">
              <Text className="--score" variant="title3XL">
                {trustScore}
              </Text>
              <div className="--logo-wrapper">
                <TrustpilotSvg />
              </div>
              <img src={getRateURL(stars)} alt={scoreAlt} className="--stars" />
            </div>
            <div className="--reviews-block">
              <Text className="--reviews" variant="bodyM">
                {formatMessage(TRUSTPILOT_LABELS.verifiedReviews, {
                  reviews: numberOfReviews
                })}
              </Text>
            </div>
          </HeroWrapper>
        </NoStyleLink>
      )

    case 'summary':
    default:
      return (
        <SummaryWrapper
          className={`--theme-${theme}`}
          onClick={() => onClick?.()}
          href={TRUSTPILOT_REVIEWS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="--row">
            <Text className="--score-label">
              {formatMessage(getTrustScoreLabel(trustScore))}
            </Text>
            <img src={getRateURL(stars)} alt={scoreAlt} className="--stars" />
            <TrustpilotSvg />
          </div>
          <div className="--row">
            <Text variant="bodyXS">{trustScore}/5</Text>
            <Text variant="bodyXS">
              {formatMessage(TRUSTPILOT_LABELS.reviewsCount, {
                reviews: numberOfReviews
              })}
            </Text>
          </div>
        </SummaryWrapper>
      )
  }
}
